import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import PageShell from "../components/PageShell";
import PageHero from "../components/PageHero";
import Logo from "../components/Logo";

/* ────────────────────────────────────────────────────────────────
 *  Section helpers
 * ────────────────────────────────────────────────────────────── */
function Section({ id, label, title, kicker, children }) {
  return (
    <section
      id={id}
      data-testid={`logo-section-${id}`}
      className="py-24 md:py-32 border-t border-hairline"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="mb-12 md:mb-16 grid md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-7">
            <span className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x">
              ◇ {label}
            </span>
            <h2 className="mt-3 font-display text-4xl md:text-6xl font-medium tracking-tight text-primary-x">
              {title}
            </h2>
          </div>
          {kicker && (
            <p className="md:col-span-5 text-secondary-x leading-relaxed">{kicker}</p>
          )}
        </div>
        {children}
      </div>
    </section>
  );
}

function Tile({ label, bg, fg, span = "", children, testId, onReplay }) {
  return (
    <div
      data-testid={testId}
      className={`relative overflow-hidden rounded-2xl border border-hairline ${span}`}
      style={{ background: bg, color: fg }}
    >
      <div className="absolute top-4 left-4 right-4 flex items-center justify-between text-[10px] tracking-[0.25em] uppercase font-mono-x opacity-70 z-10">
        <span>{label}</span>
        {onReplay && (
          <button
            type="button"
            onClick={onReplay}
            data-testid={`${testId}-replay`}
            aria-label="Replay animation"
            className="inline-flex items-center gap-1.5 rounded-full border border-current/40 px-2.5 py-1 text-[10px] hover:opacity-100 opacity-80 hover:bg-white/5 transition-colors"
            style={{ borderColor: "currentColor" }}
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M3 12a9 9 0 1 0 3-6.7" />
              <path d="M3 4v5h5" />
            </svg>
            Play
          </button>
        )}
      </div>
      <div className="aspect-[16/10] md:aspect-[5/3] grid place-items-center p-8">
        {children}
      </div>
    </div>
  );
}

/** Tile that re-mounts its children when the play button is clicked, so any
 *  child motion / animation restarts from a fresh frame zero. */
function MotionTile({ label, bg, fg, span, testId, children }) {
  const [k, setK] = useState(0);
  return (
    <Tile
      label={label}
      bg={bg}
      fg={fg}
      span={span}
      testId={testId}
      onReplay={() => setK((p) => p + 1)}
    >
      {typeof children === "function" ? children(k) : <React.Fragment key={k}>{children}</React.Fragment>}
    </Tile>
  );
}

/* ────────────────────────────────────────────────────────────────
 *  Animated variants
 * ────────────────────────────────────────────────────────────── */

/** Path d's pulled out of logo.svg so we can animate strokes individually. */
const SVG_PATHS = {
  s: "M303.14,124.8c-8.96,0-16.14-1.53-21.52-4.59l1.49-10.9c2.43,1.33,5.53,2.52,9.3,3.57,3.76,1.05,7.19,1.58,10.29,1.58s5.71-.6,7.5-1.8c1.79-1.2,2.7-2.96,2.74-5.28,0-2.1-.84-3.76-2.52-4.98-1.68-1.22-4.88-2.73-9.6-4.54-1.62-.59-2.67-.98-3.15-1.16-5.61-2.18-9.65-4.6-12.12-7.27-2.47-2.67-3.71-6.3-3.71-10.87,0-5.53,2-9.77,6-12.72,4-2.95,9.58-4.43,16.74-4.43,7.75,0,14.55,1.46,20.42,4.37l-3.6,10.01c-5.79-2.69-11.31-4.04-16.54-4.04-2.91,0-5.19.49-6.83,1.47-1.64.98-2.46,2.46-2.46,4.45,0,1.88.79,3.35,2.38,4.4,1.58,1.05,4.68,2.42,9.3,4.12.15.04.63.21,1.44.5.81.29,1.44.53,1.88.72,5.57,2.03,9.67,4.45,12.29,7.28,2.62,2.82,3.93,6.5,3.93,11.04-.04,6.09-2.11,10.79-6.2,14.11-4.09,3.32-9.9,4.98-17.43,4.98Z",
  ie: "M199.15,54.03h14.55v-12.39h-14.55v12.39ZM269.51,69.25c-4.63-5.24-11.13-7.86-19.5-7.86-8.85.04-15.84,2.92-20.97,8.63-2.44,2.72-4.27,5.89-5.55,9.5,8.09-2.32,15.63-4.45,20.5-6.38,1.82-.93,3.84-1.41,6.08-1.41,3.98,0,7.1,1.33,9.35,3.98,2.25,2.65,3.38,6.45,3.38,11.4h-26.56s0-.07.01-.11h-14.5c-.25,1.94-.4,3.96-.4,6.09,0,9.92,2.72,17.7,8.16,23.33,5.44,5.63,13.05,8.44,22.83,8.44,8.41,0,15.66-1.88,21.74-5.64l-2.05-10.29c-6.12,3.47-12.51,5.2-19.14,5.2-5.2,0-9.29-1.56-12.26-4.68-2.97-3.12-4.53-7.52-4.67-13.2h40.45c0-.66,0-1.69.03-3.1.02-1.4.03-2.41.03-3.04,0-8.67-2.31-15.62-6.95-20.86ZM199.15,123.7h14.5v-36.69h-13.91c4.21-1.6,9.01-3.13,13.91-4.61v-19.51h-14.5v60.81Z",
  t: "M183.2,124.53c-4.13,0-7.57-.49-10.32-1.47-2.75-.98-4.9-2.52-6.44-4.62-1.55-2.1-2.65-4.58-3.29-7.44-.65-2.86-.97-6.39-.97-10.6v-28.72h-9.96l.55-7.86,10.4-.94,4.65-16.49,8.85-.06v16.55h15.11v8.8h-15.11v28.99c0,4.61.66,7.89,1.99,9.85,1.33,1.96,3.78,2.93,7.36,2.93,2.1,0,4.35-.18,6.75-.55l.39,10.4c-.08,0-.53.08-1.36.25-.83.16-1.62.31-2.38.44-.76.13-1.72.25-2.91.36-1.18.11-2.29.16-3.32.16Z",
  l: "M146.01,124.53c-4.17,0-7.63-.49-10.38-1.47-2.75-.98-4.92-2.54-6.5-4.68-1.59-2.14-2.69-4.68-3.32-7.63-.63-2.95-.94-6.62-.94-11.01v-59.26l14.5-.94v59.92c0,5.46.64,9.16,1.93,11.09,1.29,1.94,3.8,2.91,7.53,2.91,1.84,0,4.18-.2,7.03-.61l.39,10.34s-.49.09-1.36.28c-.87.18-1.7.34-2.49.47-.8.13-1.8.26-3.02.39-1.22.13-2.34.19-3.37.19Z",
  u1: "M28.78,48.82v65.02c0,21.93,11.38,37.5,28.31,38.75l-1.51-4.54c-4.38,3.82-8.97,6.7-13.65,8.57-8.91,3.55-17.45,3.05-24.05-1.42-8.07-5.47-12.7-16.36-12.7-29.88V47.09l-1.63,2.41,18.37-7.32s2.96-1.08,4.79.18c1.72,1.18,2.08,4.17,2.08,6.47ZM20,37.36L1.63,44.68l-1.63.65v79.98c0,15.27,5.46,27.72,14.97,34.17,8.06,5.47,18.32,6.16,28.88,1.95,5.22-2.08,10.31-5.27,15.14-9.47l4.68-4.08-6.2-.46c-14.28-1.05-23.5-14.24-23.5-33.58V48.82c0-5.18-1.47-8.81-4.37-10.77-4.08-2.77-9.05-.91-9.6-.69Z",
  u2: "M80.59,29.19v64c0,30.64-10.84,42.52-25.01,54.87l-4.69,4.08,6.2.46c5,.37,10.23-.51,15.54-2.63,26.65-10.63,41.93-35.5,41.93-68.25V0l-3.55,1.42-19.46,7.76.32-.1c-.46.12-11.28,3.06-11.28,20.11ZM93.47,13.99l19.46-7.76-3.55-2.41v77.88c0,30.52-14.09,53.64-38.66,63.43-4.57,1.82-9.03,2.59-13.24,2.28l1.51,4.54c14.66-12.77,26.79-26.01,26.79-58.78V29.19c0-12.83,7.1-15.01,7.4-15.1l.13-.04s.16-.06.16-.06Z",
};

/** Stroke-draw animated logo: paths draw in one after another. */
function LogoDrawIn({ variant = "wordmark", className = "", repeatKey }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: false, margin: "-30% 0px" });
  const paths = variant === "mark"
    ? ["u1", "u2"]
    : variant === "wordmark"
      ? ["u1", "u2", "l", "t", "ie", "s"]
      : ["u1", "u2", "l", "t", "ie", "s"];
  const viewBox = variant === "mark" ? "0 0 116 162" : "0 0 326.76 162";
  return (
    <div ref={ref} className={className} key={repeatKey}>
      <svg viewBox={viewBox} className="w-full h-full" style={{ color: "currentColor" }} aria-hidden>
        {paths.map((k, i) => (
          <motion.path
            key={k}
            d={SVG_PATHS[k]}
            fill="transparent"
            stroke="currentColor"
            strokeWidth={1.2}
            initial={{ pathLength: 0, fillOpacity: 0 }}
            animate={
              inView
                ? { pathLength: 1, fillOpacity: 1 }
                : { pathLength: 0, fillOpacity: 0 }
            }
            transition={{
              pathLength: { delay: i * 0.18, duration: 1.0, ease: [0.22, 1, 0.36, 1] },
              fillOpacity: { delay: i * 0.18 + 0.9, duration: 0.5 },
            }}
          />
        ))}
      </svg>
    </div>
  );
}

/** Outline-only logo (no fill ever) — useful for embossed / etched look. */
function LogoOutline({ variant = "wordmark", className = "" }) {
  const paths = variant === "mark"
    ? ["u1", "u2"]
    : ["u1", "u2", "l", "t", "ie", "s"];
  const viewBox = variant === "mark" ? "0 0 116 162" : "0 0 326.76 162";
  return (
    <svg viewBox={viewBox} className={className} style={{ color: "currentColor" }} aria-hidden>
      {paths.map((k) => (
        <path key={k} d={SVG_PATHS[k]} fill="none" stroke="currentColor" strokeWidth={1.2} />
      ))}
    </svg>
  );
}

/** Mouse-magnetic logo — drifts toward cursor inside its box. */
function LogoMagnetic({ className }) {
  const ref = useRef(null);
  const [t, setT] = useState({ x: 0, y: 0 });
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    setT({
      x: ((e.clientX - r.left) / r.width - 0.5) * 30,
      y: ((e.clientY - r.top) / r.height - 0.5) * 30,
    });
  };
  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => setT({ x: 0, y: 0 })}
      className="w-full h-full grid place-items-center"
    >
      <motion.div
        animate={{ x: t.x, y: t.y }}
        transition={{ type: "spring", stiffness: 200, damping: 18 }}
      >
        <Logo variant="mark" className={className} />
      </motion.div>
    </div>
  );
}

/** Glitch logo — alternating offset cyan/magenta copies. */
function LogoGlitch({ className }) {
  return (
    <div className="relative grid place-items-center w-full h-full">
      <Logo variant="wordmark" className={className} style={{ color: "currentColor" }} />
      <motion.div
        className="absolute"
        animate={{ x: [0, -3, 2, 0, -2, 0], y: [0, 1, -1, 0] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        style={{ mixBlendMode: "screen" }}
      >
        <Logo variant="wordmark" className={className} style={{ color: "#22d3ee" }} />
      </motion.div>
      <motion.div
        className="absolute"
        animate={{ x: [0, 3, -2, 0, 2, 0], y: [0, -1, 1, 0] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        style={{ mixBlendMode: "screen" }}
      >
        <Logo variant="wordmark" className={className} style={{ color: "#ec4899" }} />
      </motion.div>
    </div>
  );
}

/** Pulse / heartbeat — slow soft scale + opacity. */
function LogoPulse({ className }) {
  return (
    <motion.div
      animate={{ scale: [1, 1.05, 1], opacity: [0.92, 1, 0.92] }}
      transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
      className="grid place-items-center w-full h-full"
    >
      <Logo variant="mark" className={className} />
    </motion.div>
  );
}

/** Loading spinner — mark rotates inside a thin progress ring. */
function LogoLoader({ className }) {
  return (
    <div className="relative grid place-items-center w-full h-full">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
      >
        <Logo variant="mark" className={className} />
      </motion.div>
      <svg viewBox="0 0 100 100" className="absolute inset-0 m-auto h-[140%] w-[140%] -z-0">
        <motion.circle
          cx="50"
          cy="50"
          r="45"
          fill="none"
          stroke="currentColor"
          strokeWidth="0.6"
          strokeLinecap="round"
          strokeDasharray="60 240"
          initial={{ rotate: 0 }}
          animate={{ rotate: 360 }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "linear" }}
          style={{ originX: "50%", originY: "50%", color: "currentColor", opacity: 0.5 }}
        />
      </svg>
    </div>
  );
}

/** Marquee — logo repeats in a horizontal scroll. */
function LogoMarquee() {
  const items = new Array(8).fill(0);
  return (
    <div className="relative overflow-hidden w-full h-full grid place-items-center">
      <div className="flex whitespace-nowrap marquee items-center">
        {items.concat(items).map((_, i) => (
          <span key={i} className="px-8 flex items-center text-primary-x">
            <Logo variant="wordmark" className="h-10 w-auto" />
            <span className="ml-8 text-muted-x">◇</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/** Theme-flip — alternates dark↔light look every 3s. */
function LogoFlip() {
  const [dark, setDark] = useState(true);
  useEffect(() => {
    const id = setInterval(() => setDark((d) => !d), 2400);
    return () => clearInterval(id);
  }, []);
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={dark ? "d" : "l"}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.45 }}
        className="w-full h-full grid place-items-center rounded-2xl"
        style={{
          background: dark ? "#050505" : "#fafafa",
          color: dark ? "#ffffff" : "#0a0a0a",
        }}
      >
        <Logo variant="full" className="h-24 w-auto" />
      </motion.div>
    </AnimatePresence>
  );
}

/* ────────────────────────────────────────────────────────────────
 *  Page
 * ────────────────────────────────────────────────────────────── */

export default function LogoPage() {
  const [redraw, setRedraw] = useState(0);

  return (
    <PageShell>
      <PageHero
        eyebrow="◇ Brand"
        title="The Ulties logo."
        accent="logo."
        kicker="One mark, many uses. Below: every approved variation — with and without the wordmark, with and without the tagline, plus a small library of motion treatments."
      />

      {/* Section 1 — Variants */}
      <Section
        id="variants"
        label="Variants"
        title="Three. That's it."
        kicker="The mark stands alone, the wordmark adds 'lties', the full lockup adds the 'tech solutions' tagline. Never invent a fourth."
      >
        <div className="grid md:grid-cols-3 gap-6">
          <Tile testId="variant-mark" label="MARK ONLY" bg="var(--surface-2)" fg="var(--text-primary)">
            <Logo variant="mark" className="h-28 md:h-36 w-auto" />
          </Tile>
          <Tile testId="variant-wordmark" label="WORDMARK" bg="var(--surface-2)" fg="var(--text-primary)">
            <Logo variant="wordmark" className="h-20 md:h-24 w-auto" />
          </Tile>
          <Tile testId="variant-full" label="FULL LOCKUP" bg="var(--surface-2)" fg="var(--text-primary)">
            <Logo variant="full" className="h-24 md:h-32 w-auto" />
          </Tile>
        </div>
      </Section>

      {/* Section 2 — Color treatments */}
      <Section
        id="colors"
        label="Color"
        title="On any surface."
        kicker="The SVG is filled with currentColor, so the logo takes the color of whatever sits around it. These are the approved combinations."
      >
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Tile testId="color-mono-dark" label="MONO · DARK BG" bg="#050505" fg="#ffffff">
            <Logo variant="wordmark" className="h-20 w-auto" />
          </Tile>
          <Tile testId="color-mono-light" label="MONO · LIGHT BG" bg="#fafafa" fg="#0a0a0a">
            <Logo variant="wordmark" className="h-20 w-auto" />
          </Tile>
          <Tile testId="color-brand-emerald" label="ON SIGNAL GREEN" bg="#022c22" fg="#34d399">
            <Logo variant="wordmark" className="h-20 w-auto" />
          </Tile>
          <Tile testId="color-outline" label="OUTLINE / ETCHED" bg="var(--surface-2)" fg="var(--text-primary)">
            <LogoOutline variant="wordmark" className="h-20 w-auto" />
          </Tile>
          <Tile testId="color-duotone" label="DUOTONE OVERLAY" bg="#0a0a0a" fg="#ffffff">
            <div
              className="grid place-items-center w-full h-full"
              style={{
                WebkitMaskImage: "linear-gradient(135deg, #fff 30%, transparent 70%)",
                maskImage: "linear-gradient(135deg, #fff 30%, transparent 70%)",
              }}
            >
              <Logo variant="wordmark" className="h-20 w-auto" style={{ color: "#a78bfa" }} />
            </div>
          </Tile>
          <Tile testId="color-glow" label="GLOW · NIGHT" bg="#050505" fg="#ffffff">
            <div
              className="grid place-items-center w-full h-full"
              style={{ filter: "drop-shadow(0 0 32px rgba(255,255,255,0.45))" }}
            >
              <Logo variant="wordmark" className="h-20 w-auto" />
            </div>
          </Tile>
        </div>
      </Section>

      {/* Section 3 — Tagline lockups */}
      <Section
        id="taglines"
        label="Sub-text"
        title="With and without."
        kicker="The tagline is optional. Use the full lockup in moments of brand context (footers, presentations). Use the wordmark or mark in functional UI."
      >
        <div className="grid md:grid-cols-2 gap-6">
          <Tile testId="tag-with" label="WITH SUB-TEXT" bg="var(--surface-2)" fg="var(--text-primary)" span="md:col-span-1">
            <Logo variant="full" className="h-32 md:h-40 w-auto" />
          </Tile>
          <Tile testId="tag-without" label="NO SUB-TEXT" bg="var(--surface-2)" fg="var(--text-primary)" span="md:col-span-1">
            <Logo variant="wordmark" className="h-20 md:h-24 w-auto" />
          </Tile>
        </div>

        <div className="mt-6 grid md:grid-cols-3 gap-6">
          <Tile testId="tag-stacked-1" label="STACKED · CENTERED" bg="#050505" fg="#ffffff">
            <div className="flex flex-col items-center gap-3">
              <Logo variant="mark" className="h-20 w-auto" />
              <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-400 font-mono-x">
                ULTIES · TECH SOLUTIONS
              </div>
            </div>
          </Tile>
          <Tile testId="tag-side" label="MARK · SIDE TAGLINE" bg="#050505" fg="#ffffff">
            <div className="flex items-center gap-4">
              <Logo variant="mark" className="h-16 w-auto" />
              <div className="text-xs tracking-[0.25em] uppercase text-zinc-400 font-mono-x leading-tight">
                Ulties<br /><span className="text-zinc-500">tech solutions</span>
              </div>
            </div>
          </Tile>
          <Tile testId="tag-with-rule" label="WORDMARK · RULE" bg="#050505" fg="#ffffff">
            <div className="flex flex-col items-center gap-3">
              <Logo variant="wordmark" className="h-16 w-auto" />
              <span className="h-px w-32 bg-white/30" />
              <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-400 font-mono-x">
                EST. 2019 · LISBON
              </div>
            </div>
          </Tile>
        </div>
      </Section>

      {/* Section 4 — Motion */}
      <Section
        id="motion"
        label="Motion"
        title="Logo, in motion."
        kicker="Eight approved motion treatments. Each card has its own ▶ Play button — use it to replay the animation from frame zero."
      >
        <div className="mb-8">
          <button
            type="button"
            onClick={() => setRedraw((k) => k + 1)}
            data-testid="logo-replay"
            className="inline-flex items-center gap-2 rounded-full border border-hairline-strong px-4 py-2 text-xs text-primary-x hover-glass transition-colors"
          >
            ↻ Replay all animations
          </button>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <MotionTile testId="motion-draw-mark" label="STROKE DRAW · MARK" bg="#050505" fg="#ffffff">
            {(k) => (
              <LogoDrawIn variant="mark" className="h-28 w-auto" repeatKey={`${redraw}-${k}`} />
            )}
          </MotionTile>
          <MotionTile testId="motion-draw-wordmark" label="STROKE DRAW · WORDMARK" bg="#050505" fg="#ffffff">
            {(k) => (
              <LogoDrawIn variant="wordmark" className="w-full max-w-md h-24" repeatKey={`${redraw}-${k}`} />
            )}
          </MotionTile>
          <MotionTile testId="motion-pulse" label="PULSE / HEARTBEAT" bg="#050505" fg="#ffffff">
            <LogoPulse className="h-24 w-auto" />
          </MotionTile>
          <MotionTile testId="motion-magnetic" label="MAGNETIC · CURSOR" bg="#050505" fg="#ffffff">
            <LogoMagnetic className="h-24 w-auto" />
          </MotionTile>
          <MotionTile testId="motion-glitch" label="CHROMATIC GLITCH" bg="#050505" fg="#ffffff">
            <LogoGlitch className="h-20 w-auto" />
          </MotionTile>
          <MotionTile testId="motion-loader" label="LOADER" bg="#050505" fg="#ffffff">
            <LogoLoader className="h-16 w-auto" />
          </MotionTile>
          <MotionTile testId="motion-marquee" label="MARQUEE" bg="#050505" fg="#ffffff" span="md:col-span-2 lg:col-span-2">
            <LogoMarquee />
          </MotionTile>
          <MotionTile testId="motion-flip" label="THEME FLIP" bg="transparent" fg="currentColor">
            <LogoFlip />
          </MotionTile>
        </div>
      </Section>

      {/* Section 5 — Sizes */}
      <Section
        id="sizes"
        label="Sizes"
        title="From favicon to billboard."
        kicker="Minimum heights below ensure the wordmark stays legible. Always allow ¼ × the mark height of clearspace on all sides."
      >
        <div className="rounded-2xl border border-hairline bg-surface-2 divide-y divide-hairline">
          {[
            { l: "16 px · favicon", v: "mark", h: "h-4" },
            { l: "24 px · UI icon", v: "mark", h: "h-6" },
            { l: "40 px · nav", v: "wordmark", h: "h-10" },
            { l: "64 px · page header", v: "wordmark", h: "h-16" },
            { l: "120 px · footer lockup", v: "full", h: "h-[120px]" },
            { l: "240 px · hero brand moment", v: "full", h: "h-[240px]" },
          ].map((row) => (
            <div
              key={row.l}
              className="grid grid-cols-[160px_1fr] items-center gap-6 px-6 py-6"
            >
              <div className="text-[11px] tracking-[0.22em] uppercase text-muted-x font-mono-x">
                {row.l}
              </div>
              <div>
                <Logo variant={row.v} className={`${row.h} w-auto text-primary-x`} />
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Section 6 — Don'ts */}
      <Section
        id="donts"
        label="Don'ts"
        title="Please don't."
        kicker="A short list of things that will get the logo retired from your design."
      >
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Tile testId="dont-stretch" label="DON'T STRETCH" bg="#1f0a0a" fg="#fca5a5">
            <Logo variant="wordmark" className="h-16 w-72" style={{ color: "#ffffff" }} />
          </Tile>
          <Tile testId="dont-recolor" label="DON'T RAINBOW" bg="#1f0a0a" fg="#fca5a5">
            <Logo
              variant="wordmark"
              className="h-16 w-auto"
              style={{
                background:
                  "linear-gradient(90deg,#ef4444,#f59e0b,#10b981,#3b82f6,#8b5cf6)",
                WebkitBackgroundClip: "text",
                color: "transparent",
                // also fill the SVG paths with the gradient via mask
                WebkitMaskImage: "none",
              }}
            />
          </Tile>
          <Tile testId="dont-tilt" label="DON'T TILT" bg="#1f0a0a" fg="#fca5a5">
            <div style={{ transform: "rotate(-12deg)" }}>
              <Logo variant="wordmark" className="h-16 w-auto" style={{ color: "#ffffff" }} />
            </div>
          </Tile>
          <Tile testId="dont-shadow" label="DON'T DROP SHADOW" bg="#1f0a0a" fg="#fca5a5">
            <Logo
              variant="wordmark"
              className="h-16 w-auto"
              style={{
                color: "#ffffff",
                filter: "drop-shadow(6px 6px 0 #ef4444)",
              }}
            />
          </Tile>
        </div>
      </Section>

      {/* Footer CTA */}
      <section className="border-t border-hairline py-24 md:py-32 text-center bg-surface">
        <div className="max-w-3xl mx-auto px-6">
          <h3 className="font-display text-4xl md:text-6xl font-medium tracking-tight text-primary-x">
            Need the assets?
          </h3>
          <p className="mt-4 text-secondary-x">
            Download SVG, PNG and the brand guidelines pack.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a
              href="/logo.svg"
              download
              data-testid="logo-download-svg"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium hover:opacity-90 transition-opacity"
              style={{ background: "var(--text-primary)", color: "var(--canvas)" }}
            >
              Download SVG ↓
            </a>
            <a
              href="/logo.svg"
              target="_blank"
              rel="noreferrer"
              data-testid="logo-open-svg"
              className="inline-flex items-center gap-2 rounded-full border border-hairline-strong px-5 py-2.5 text-sm text-primary-x hover-glass transition-colors"
            >
              Open raw SVG ↗
            </a>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
